import React, { useState, useEffect, useRef } from "react";
import allApi from "../../../../api/allApi";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";

const accessPointsList = [
  // ... (same as your current code)
  // [ ... accessPointsList array ... ]
];

const generateAPConnections = () => {
  // ... (same as your current code)
  // [ ... generateAPConnections function ... ]
};

const getRSSIColor = (value) => {
  const rssi = Number(value);

  // 30–50 → GOOD (Green shades)
  if (rssi >= 30 && rssi < 50) return "#22c55e"; // Green

  // 50–70 → WARNING (Yellow shades)
  if (rssi >= 50 && rssi < 70) return "#facc15"; // Yellow

  // 70–80 → BAD (Orange shades)
  if (rssi >= 70 && rssi < 80) return "#f97316"; // Orange

  // 80–90 → VERY BAD (Red shades)
  if (rssi >= 80) return "#ef4444"; // Red

  return "#9ca3af"; // fallback
};

const getStatusColor = (status) => {
  switch (status) {
    case "Good":
      return "#22c55e";
    case "Warning":
      return "#f59e0b";
    case "Bad":
      return "#ef4444";
    default:
      return "#6b7280";
  }
};

const getConnectionStyle = (strength, rssi) => {
  switch (strength) {
    case "strong":
      return { color: "#22c55e", width: 3, opacity: 0.8, dashArray: "none" };
    case "medium":
      return { color: "#f59e0b", width: 2, opacity: 0.7, dashArray: "5,5" };
    case "weak":
      return { color: "#ef4444", width: 1.5, opacity: 0.6, dashArray: "3,3" };
    default:
      return { color: "#6b7280", width: 1, opacity: 0.5, dashArray: "2,2" };
  }
};

function ClientRoaming() {
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredConnection, setHoveredConnection] = useState(null);
  const [chartDimensions, setChartDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [networkNodes, setNetworkNodes] = useState({
    clients: [],
    accessPoints: [],
  });
  const [clientsList, setClientsList] = useState([]);
  const {
    selectedClient,
    clientTrends = [],
    defaultInterval,
  } = location.state || {};

  useEffect(() => {
    const type = String(selectedClient?.clientType || "").toLowerCase();
    if (type === "wired") {
      navigate("/client", { replace: true });
    }
  }, [selectedClient, navigate]);

  const [interval, setInterval] = useState(defaultInterval || "48hr");
  const [apList, setApList] = useState([]);
  const [selectedAP, setSelectedAP] = useState(null);
  const [filteredTrends, setFilteredTrends] = useState([]);

  const chartRef = useRef(null);

  // Prepare chart data for RSSI trend
  const chartData = filteredTrends.map((item) => ({
    time: item.interval1,
    rssi: item.rssi,
  }));

  useEffect(() => {
    if (!clientTrends.length) return;

    const apMap = {};

    clientTrends.forEach((item) => {
      if (!apMap[item.deviceId]) {
        apMap[item.deviceId] = {
          deviceId: item.deviceId,
          serialNumber: item.serialNumber,
          ssid: item.ssid,
          mac: item.mac,
          data: [],
        };
      }
      apMap[item.deviceId].data.push(item);
    });

    const aps = Object.values(apMap);

    setApList(aps);

    // ✅ auto-select first AP
    setSelectedAP(aps[0]);
    setFilteredTrends(aps[0].data);
  }, [clientTrends]);

     const rssiCounts = filteredTrends.reduce(
      (acc, item) => {
        const rssi = Number(item.rssi);

        if (rssi >= 30 && rssi < 50) {
          acc.good += 1;
        } else if (rssi >= 50 && rssi < 70) {
          acc.warning += 1;
        } else if (rssi >= 70) {
          acc.bad += 1;
        }

        return acc;
      },
      { good: 0, warning: 0, bad: 0 }
    );

  const fetchClientTrends = async (deviceName, interval) => {
    try {
      const api = new allApi();

      const response = await api.getRequest(
        `/client-roaming?deviceName=${deviceName}&interval=${interval}`
      );

      // adjust if your API response shape differs
      return response?.data || [];
    } catch (error) {
      console.error("Failed to fetch client roaming trends", error);
      return [];
    }
  };
  const handleIntervalChange = async (val) => {
    setInterval(val);

    const updatedData = await fetchClientTrends(selectedClient.deviceName, val);

    navigate(".", {
      replace: true,
      state: {
        ...location.state,
        clientTrends: updatedData,
        defaultInterval: val,
      },
    });
  };

  useEffect(() => {
    if (!selectedClient) return;

    const apName =
      selectedClient.connDeviceName ||
      selectedClient.apName ||
      selectedClient.connectedAp ||
      selectedClient.deviceName;

    if (apName) {
      setClientsList([apName]);
    }
  }, [selectedClient]);

  useEffect(() => {
    const updateDimensions = () => {
      if (chartRef.current) {
        const rect = chartRef.current.getBoundingClientRect();
        setChartDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  useEffect(() => {
    if (selectedClient?.connDeviceName) {
      setClientsList([selectedClient.connDeviceName]);
    }
  }, [selectedClient]);

  const handleNodeClick = (nodeId) => {
    setSelectedNode(nodeId);
    setSelectedAP(nodeId);
  };

  const handleAPListClick = (apId) => {
    setSelectedAP(apId);
    setSelectedNode(apId);
  };

  // const handleIntervalChange = (value) => {
  //   setInterval(value);
  //   console.log("Selected interval:", value);
  //   // API call can be added later if needed
  // };

  // Custom node component
  const CustomNode = ({ cx, cy, payload }) => {
    const isAP = payload.type === "ap";
    const isSelected = selectedNode === payload.id;
    const rssiColor = getRSSIColor(payload.rssi);

    return (
      <g>
        {isAP ? (
          <>
            {/* AP Rectangle */}
            <rect
              x={cx - 25}
              y={cy - 8}
              width={50}
              height={16}
              fill={payload.color}
              stroke={isSelected ? "#000" : "#fff"}
              strokeWidth={isSelected ? 3 : 1}
              rx={2}
              style={{ cursor: "pointer" }}
              onClick={() => handleNodeClick(payload.id)}
            />

            {/* RSSI indicator */}
            <circle
              cx={cx + 20}
              cy={cy - 4}
              r={3}
              fill={rssiColor}
              stroke="#fff"
              strokeWidth={1}
            />
          </>
        ) : (
          /* Client Circle */
          <circle
            cx={cx}
            cy={cy}
            r={8}
            fill={payload.color}
            stroke={getRSSIColor(payload.rssi)}
            strokeWidth={isSelected ? 4 : 2}
            style={{ cursor: "pointer" }}
            onClick={() => handleNodeClick(payload.id)}
          />
        )}

        {/* Label */}
        <text
          x={cx}
          y={cy - (isAP ? 18 : 18)}
          textAnchor="middle"
          fontSize="10"
          fill="#374151"
          fontWeight="600"
        >
          {payload.name}
        </text>
      </g>
    );
  };

  // Convert data coordinates to SVG coordinates
  const convertToSVGCoords = (dataX, dataY) => {
    const margin = { top: 20, right: 40, left: 20, bottom: 40 };
    const dataWidth = 600; // XAxis domain max
    const dataHeight = 300; // YAxis domain max

    const svgWidth = chartDimensions.width - margin.left - margin.right;
    const svgHeight = chartDimensions.height - margin.top - margin.bottom;

    const x = margin.left + (dataX / dataWidth) * svgWidth;
    const y = margin.top + ((dataHeight - dataY) / dataHeight) * svgHeight;

    return { x, y };
  };

  const ConnectionLine = ({ connection, index }) => {
    const isHovered = hoveredConnection === index;
    const style = getConnectionStyle(connection.strength, connection.rssi);

    const fromCoords = convertToSVGCoords(connection.from.x, connection.from.y);
    const toCoords = convertToSVGCoords(connection.to.x, connection.to.y);

    const strokeWidth = isHovered ? style.width + 1 : style.width;
    const opacity = isHovered ? 1 : style.opacity;

    return (
      <g key={index}>
        {/* Connection line */}
        <line
          x1={fromCoords.x}
          y1={fromCoords.y}
          x2={toCoords.x}
          y2={toCoords.y}
          stroke={style.color}
          strokeWidth={strokeWidth}
          strokeDasharray={style.dashArray}
          opacity={opacity}
          style={{ cursor: "pointer" }}
          onMouseEnter={() => setHoveredConnection(index)}
          onMouseLeave={() => setHoveredConnection(null)}
        />

        {/* Signal strength indicator on line */}
        {connection.type === "ap-ap" && (
          <circle
            cx={(fromCoords.x + toCoords.x) / 2}
            cy={(fromCoords.y + toCoords.y) / 2}
            r={3}
            fill={style.color}
            opacity={0.8}
          />
        )}

        {/* RSSI label for hovered connections */}
        {isHovered && (
          <text
            x={(fromCoords.x + toCoords.x) / 2}
            y={(fromCoords.y + toCoords.y) / 2 - 10}
            textAnchor="middle"
            fontSize="10"
            fill={style.color}
            fontWeight="600"
            style={{ background: "rgba(255,255,255,0.9)", padding: "2px" }}
          >
            {connection.rssi}dBm
          </text>
        )}
      </g>
    );
  };

  const allNodes = [
    ...(networkNodes.accessPoints || []),
    ...(networkNodes.clients || []),
  ];

  const clientConnections = (networkNodes?.clients || [])
    .map((client) => {
      const connectedAP = (networkNodes?.accessPoints || []).find(
        (ap) => ap.id === client.connectedTo
      );

      if (!connectedAP) return null;

      return {
        from: { x: client.x, y: client.y },
        to: { x: connectedAP.x, y: connectedAP.y },
        clientId: client.id,
        apId: connectedAP.id,
        rssi: client.rssi,
        type: "client-ap",
        strength:
          Math.abs(client.rssi) < 50
            ? "strong"
            : Math.abs(client.rssi) < 60
            ? "medium"
            : "weak",
      };
    })
    .filter(Boolean);

  const apConnections = generateAPConnections?.() || [];

  const allConnections = [...clientConnections, ...apConnections];

  const CustomRSSIDot = ({ cx, cy, payload }) => {
    if (cx == null || cy == null) return null;

    const size = 14;
    const color = getRSSIColor(payload.rssi);

    return (
      <g>
        <rect
          x={cx - size / 2}
          y={cy - size / 2}
          width={size}
          height={size}
          rx={3}
          fill={color}
          style={{
            filter: `drop-shadow(0px 0px 6px ${color})`,
          }}
        />
      </g>
    );
  };

  const hasClientRoamingData =
    Array.isArray(clientTrends) && clientTrends.length > 0;

  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        padding: 16,
        background: "#f7f9fc",
        height: "100%",
      }}
    >
      {/* 🔹 LEFT SIDEBAR — Associated APs */}
      <div
        style={{
          width: 280,
          minWidth: 280,
        }}
      >
        <div className="inc-card">
          <div className="inc-card-title">
            <h3>📡 Associated APs</h3>
          </div>

          <div className="inc-card-body">
            {apList.length > 0 ? (
              apList.map((ap) => (
                <div
                  key={ap.deviceId}
                  onClick={() => {
                    setSelectedAP(ap);
                    setFilteredTrends(ap.data);
                  }}
                  style={{
                    padding: "8px 12px",
                    marginBottom: 6,
                    borderRadius: 6,
                    cursor: "pointer",
                    background:
                      selectedAP?.deviceId === ap.deviceId
                        ? "#e5f0ff"
                        : "#f9fafb",
                    fontWeight:
                      selectedAP?.deviceId === ap.deviceId ? 600 : 400,
                  }}
                >
                  {ap.ssid}
                  <div style={{ fontSize: 11, color: "#6b7280" }}>
                    {ap.serialNumber}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ color: "#6b7280", fontSize: 14 }}>No APs found</div>
            )}
          </div>
        </div>
      </div>

      {/* 🔹 RIGHT — FULL WIDTH GRAPH CARD */}
      <div style={{ flex: 1 }}>
        <div
          className="inc-card"
          style={{ height: "100%", position: "relative" }}
        >
          {/* Header */}
          <div
            className="inc-card-title"
            style={{ display: "flex", alignItems: "center" }}
          >
            <h3 style={{ flex: 1 }}>
              Client RSSI Trend – {selectedAP?.ssid || "-"}
            </h3>

            <div className="summary-dropdown">
              <div>
                <span
                  className="dropdown"
                  id="intervalDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span>
                    {interval}
                    <i className="fa fa-angle-down" aria-hidden="true"></i>
                  </span>

                  <ul
                    className="dropdown-menu dropdown-menu-end"
                    aria-labelledby="intervalDropdown"
                  >
                    <li>
                      <a
                        className="dropdown-item"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleIntervalChange("8hr");
                        }}
                      >
                        8 Hours
                      </a>
                    </li>

                    <li>
                      <a
                        className="dropdown-item"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleIntervalChange("12hr");
                        }}
                      >
                        12 Hours
                      </a>
                    </li>

                    <li>
                      <a
                        className="dropdown-item"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleIntervalChange("24hr");
                        }}
                      >
                        24 Hours
                      </a>
                    </li>

                    <li>
                      <a
                        className="dropdown-item"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleIntervalChange("48hr");
                        }}
                      >
                        48 Hours
                      </a>
                    </li>
                  </ul>
                </span>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div style={{ marginTop: 26, position: "relative" }}>
            {!hasClientRoamingData && (
              <div
                style={{
                  color: "#6b7280",
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 16,
                  textAlign: "center",
                }}
              >
                No client roaming data found
              </div>
            )}
            {hasClientRoamingData && (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart
                  data={chartData}
                  margin={{
                    top: 30, // 🔑 IMPORTANT
                    right: 30, // 🔑 IMPORTANT
                    left: 10,
                    bottom: 10,
                  }}
                >
                  <CartesianGrid stroke="#e5e7eb" />
                  <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                  <YAxis
                    domain={[
                      (dataMin) => dataMin - 2,
                      (dataMax) => dataMax + 2,
                    ]}
                    tick={{ fontSize: 12 }}
                    label={{
                      value: "RSSI (Quality)",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="rssi"
                    stroke="#9ca3af"
                    strokeWidth={1.5}
                    dot={<CustomRSSIDot />}
                    activeDot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* 🔹 RSSI SCALE BAR */}
          <div
            style={{
              margin: "16px 20px",
              padding: "12px",
              background: "#f9fafb",
              borderRadius: 8,
              fontSize: 11,
            }}
          >
            <div
              style={{
                fontWeight: 600,
                marginBottom: 8,
                color: "#374151",
              }}
            >
              RSSI Scale
            </div>

            {/* Color Bar */}
            <div
              style={{
                display: "flex",
                height: 8,
                borderRadius: 4,
                overflow: "hidden",
                marginBottom: 8,
              }}
            >
              <div style={{ flex: 2, background: "#22c55e" }} /> {/* 30–50 */}
              <div style={{ flex: 2, background: "#facc15" }} /> {/* 50–70 */}
              <div style={{ flex: 1, background: "#f97316" }} /> {/* 70–80 */}
              <div style={{ flex: 1, background: "#ef4444" }} /> {/* 80–90 */}
            </div>

            {/* Labels */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: "#6b7280",
              }}
            >
              <span>30</span>
              <span>50</span>
              <span>70</span>
              <span>80</span>
              <span>90</span>
            </div>
          </div>

          <div
            style={{
              background: "#fff",
              borderTop: "1px solid #e5e7eb",
              padding: "12px 20px",
              display: "flex",
              alignItems: "center",
              gap: 30,
              fontSize: 13,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  background: "#22c55e",
                  borderRadius: "50%",
                }}
              />
              <span>Good: {rssiCounts.good}</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  background: "#facc15",
                  borderRadius: "50%",
                }}
              />
              <span>Warning: {rssiCounts.warning}</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  background: "#ef4444",
                  borderRadius: "50%",
                }}
              />
              <span>Bad: {rssiCounts.bad}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientRoaming;
